"use client";

import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";

/**
 * A provider-neutral row for dynamic status-bearing enterprise records.
 * The copy and status share space when available and reflow independently
 * when a narrow phone, long locale string or larger font requires it.
 */
export function ResponsiveStatusRow({
  title,
  detail,
  category,
  status,
  statusClassName,
  statusDetail,
  className = "",
  children,
}: {
  title: ReactNode;
  detail?: ReactNode;
  category?: ReactNode;
  status: ReactNode;
  statusClassName?: string;
  statusDetail?: ReactNode;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <div className={`runyi-responsive-status-row flex w-full min-w-0 max-w-full flex-wrap items-start gap-x-3 gap-y-2 rounded-lg bg-foreground/[0.025] px-2.5 py-2.5 ${className}`}>
      <div className="runyi-status-copy min-w-0 flex-[1_1_12rem] break-words">
        <div className="break-words text-[11px] font-semibold leading-5">{title}</div>
        {detail != null && (
          <div className="mt-1 break-all text-[10px] leading-4 text-foreground/40">{detail}</div>
        )}
        {category != null && (
          <div className="mt-1 break-words text-[9px] uppercase tracking-wider text-foreground/40">{category}</div>
        )}
        {children}
      </div>
      <div className="runyi-status-meta flex min-w-0 max-w-full flex-[0_1_auto] flex-col items-start gap-1 sm:items-end">
        <Badge className={`max-w-full whitespace-normal break-words text-left leading-4 ${statusClassName ?? ""}`}>
          {status}
        </Badge>
        {statusDetail != null && (
          <div className="max-w-full break-words text-left text-[9px] leading-4 text-foreground/40 sm:text-right">
            {statusDetail}
          </div>
        )}
      </div>
    </div>
  );
}
